import { useEffect } from 'react';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import {
  addResearchAssistantMessages,
  resetResearchAssistantMessages,
  clearResearchAssistantIsProcessing,
  setResearchAssistantIsReady,
} from '@verantos-dev/app-state-research-assistant';
import { logError } from '@verantos-dev/core-utils';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';
import { useHandlePopulateChatHistory } from '../../hooks/useHandlePopulateChatHistory';

type HistoryMessageResponseType = {
  detail: {
    user_prompt?: string | null;
    summary?: string | null;
    created_at: string;
    model_version?: string | null;
    is_user_generated_dsl: boolean;
    cohort_action?: string | null;
    dsl?: string | null;
  };
  sender: string;
  timestamp: string;
};

export const useChatWindowListeners = () => {
  const dispatch = useAppDispatch();
  const { socket: currentSocket } = useResearchAssistantSocketContext();
  const { populateMessages } = useHandlePopulateChatHistory();

  useEffect(() => {
    const clearMessagesHandler = () => {
      dispatch(setResearchAssistantIsReady(true));
      dispatch(resetResearchAssistantMessages());
      dispatch(clearResearchAssistantIsProcessing({}));
    };

    const messagesHistoryHandler = (data: HistoryMessageResponseType) => {
      if (data.detail && Array.isArray(data.detail)) {
        populateMessages(
          data.detail.map((message) => ({
            userPrompt: message.user_prompt,
            summary: message.summary,
            createdAt: message.created_at,
            modelVersion: message.model_version,
            isUserGeneratedDsl: message.is_user_generated_dsl,
            cohortAction: message.cohort_action,
            dsl: message.dsl,
          }))
        );
      }
      dispatch(setResearchAssistantIsReady(true));
      dispatch(clearResearchAssistantIsProcessing({}));
    };

    const messageHandler = (message: any) => {
      if (message.detail) {
        dispatch(
          addResearchAssistantMessages([
            {
              message: message.detail,
              timestamp: new Date(message.timestamp).toLocaleTimeString(),
              currentUser: message.sender,
              isCurrentUser: message.sender === currentSocket?.id,
            },
          ])
        );
      }
      dispatch(setResearchAssistantIsReady(true));
      dispatch(clearResearchAssistantIsProcessing({}));
    };

    const handleError = (message: any) => {
      logError({ message });
      dispatch(
        addResearchAssistantMessages([
          {
            message: message.detail,
            timestamp: new Date().toLocaleTimeString(),
            currentUser: message.sender,
            isCurrentUser: false,
          },
        ])
      );
      dispatch(setResearchAssistantIsReady(true));
      dispatch(clearResearchAssistantIsProcessing({}));
    };

    if (currentSocket) {
      // Listen for 'clear' events from the server
      currentSocket.on('clear', clearMessagesHandler);
      // Listen for 'messageHistory' events from the server
      currentSocket.on('history', messagesHistoryHandler);
      // Listen for 'message' events from the server
      currentSocket.on('message', messageHandler);
      // Listen for 'error' events from the server
      currentSocket.on('error', handleError);
    }
    console.info('Chat window events registered');

    return () => {
      if (currentSocket) {
        currentSocket.off('clear', clearMessagesHandler);
        currentSocket.off('history', messagesHistoryHandler);
        currentSocket.off('message', messageHandler);
        currentSocket.off('error', handleError);
        console.info('Chat window events unregistered');
      }
    };
  }, [dispatch, currentSocket, currentSocket?.id, populateMessages]);
};
